// The Plazmi Gazette (/gazette, /gazette/<day>) and the village noticeboard
// (/noticeboard): the village's story, from /api/gazette and /api/noticeboard.
// Every word comes from the server, which tells it from the event log; this
// page sets it in type: wood-type headlines, woodcuts, ink rules, and real
// postcards of the homes tipped in. The noticeboard is a cork board.
import { ART } from './woodcuts.js';
import { danceCard, SCORE_CSS } from './score.js';

type Story = { kind: string; art: string; homes: string[]; headline: string; deck: string; body: string[]; beats?: string[]; dance?: string; dance_title?: string; more?: { href: string; text: string } };
type Issue = {
  summary: string; day: number; date: string; season: string; homes: number; days: number[]; weather: string;
  lead: Story; stories: Story[];
  friendships: { text: string }[]; helping: string[];
  overheard: { kind: string; who: string; where: string; text: string }[];
  garden: { slug: string; name: string; fireflies: number; text: string } | null;
  news: string[]; dances: { name: string; title: string; by: string; day: number; beats: string[]; homes: number }[];
  notices: { who: string; text: string }[];
  underway: { n: number; title: string; name?: string; site_name: string; builders: string[]; asked: string[]; wants: string; progress: number; kind: string }[];
  residents: Record<string, { name: string; emoji?: string; plot: number }>;
  almanac?: Record<string, number>;
  // the homes in the paper with a postcard drawn (a kept issue from before has none: then every home has one)
  cards?: string[];
  underway_count?: number;
};

const esc = (s: any) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
const done = () => { (window as any).__ready = true; };

// A day kept in the archive (/archive): the server sets the day's frozen data
// into the page, and this draws it as it was, with links to the days either
// side as siblings (day-<n>/gazette.html), so a kept copy reads on its own.
type Kept = { what: 'gazette' | 'noticeboard'; data: any; archive: { day: number; prev: number | null; next: number; shelf: string } };
const keptEl = document.getElementById('frozen');
const kept: Kept | null = keptEl ? JSON.parse(keptEl.textContent || 'null') : null;

// Wait for the postcards (drawn on first ask, a few seconds each) before saying the page is ready.
function readyWhenImages(root: HTMLElement) {
  const imgs = [...root.querySelectorAll('img')];
  let left = imgs.length;
  const one = () => { if (--left <= 0) done(); };
  if (!left) return done();
  for (const im of imgs) {
    if (im.complete && im.naturalWidth) one();
    else { im.addEventListener('load', one); im.addEventListener('error', () => { retry(im) || one(); }); }
  }
  setTimeout(done, 40000);
}
// a postcard still being drawn answers 503; try again a little later
const tries = new WeakMap<HTMLImageElement, number>();
function retry(im: HTMLImageElement) {
  const n = (tries.get(im) || 0) + 1;
  if (n > 6) return false;
  tries.set(im, n);
  setTimeout(() => { im.src = im.src.replace(/(\?.*)?$/, `?t=${n}`); }, 2500);
  return true;
}

const art = (name: string, cls = '') => `<span class="${cls}">${(ART[name] || ART.house)()}</span>`;
const postcard = (slug: string, who: string, cls = '') =>
  `<figure class="tipped ${cls}"><img src="/@${encodeURIComponent(slug)}/postcard.png" alt="${esc(who)}'s house"><figcaption>${esc(who)}'s, on the spiral</figcaption></figure>`;
const tally = (p: number) => { const n = Math.round(p * 10); return '■'.repeat(n) + '□'.repeat(10 - n); };
// a dance's beats as chips; a run of the same beat is one chip with a count (hop ×24)
function runs(bs: string[]) {
  const out: [string, number][] = [];
  for (const b of bs) { const last = out[out.length - 1]; if (last && last[0] === b) last[1]++; else out.push([b, 1]); }
  return out;
}
const steps = (bs: string[] = []) =>
  `<div class="steps">${runs(bs).map(([b, n]) => `<span>${esc(b)}${n > 1 ? `<i class="times">×${n}</i>` : ''}</span>`).join('')}</div>`;
// a dance's notation, headed and linked (score.ts); a story from before stories named their dance keeps plain chips
const storyBeats = (s: Story) => !s.beats ? '' : s.dance ? danceCard({ name: s.dance, title: s.dance_title, beats: s.beats }, { kept: !!kept }) : steps(s.beats);
const KICK: Record<string, string> = {
  project: 'Built together', started: 'Under way', festival: 'A festival', arrivals: 'Comings and goings',
  dance: 'The dance column', friends: 'Neighbourly', quiet: 'All quiet',
  dinner: 'Round the table', craft: 'Made by hand', market: 'At the market', machine: 'Machines',
  limited: 'This season', meeting: 'Well met',
};

// ---------------------------------------------------------------------------
// the Gazette

// the day in numbers, for the masthead's right ear
function almanac(g: Issue) {
  const a = g.almanac || {};
  const bits = ([['visits', 'visit'], ['dances', 'dance'], ['songs', 'song'], ['guestbook', 'guestbook line'], ['lanterns', 'lantern'], ['hands', 'hand lent']] as [string, string][])
    .filter(([k]) => a[k] > 0).map(([k, w]) => `${a[k]} ${w}${a[k] === 1 ? '' : 's'}`);
  return `${g.homes} homes on the spiral<br>${bits.slice(0, 4).join(' · ') || 'a quiet day'}`;
}

// a postcard nobody has asked for yet is drawn on first ask: ask quietly, so it's there next time
function warm(slug: string) { if (!kept) new Image().src = `/@${encodeURIComponent(slug)}/postcard.png`; }
const more = (s: Story) => s.more ? `<p class="more"><a href="${esc(s.more.href)}">${esc(s.more.text)} →</a></p>` : '';

function gazette(g: Issue) {
  const paper = document.getElementById('paper')!;
  document.title = `The Plazmi Gazette, Day ${g.day}`;
  const name = (slug: string) => g.residents[slug]?.name || slug;
  // only a home whose card is drawn goes in as a photograph; never a "being drawn" plate
  const carded = new Set(g.cards || []);
  const hasCard = (slug: string) => !g.cards || carded.has(slug);
  const lead = g.lead;
  const leadHome = lead.homes.find(hasCard);
  // a quiet day's lead names nobody: the first home any story names, so there's still somewhere to go
  const firstHome = lead.homes[0] || g.stories.flatMap((s) => s.homes)[0];
  if (!leadHome && lead.homes[0]) warm(lead.homes[0]);
  const gardenCard = !!g.garden && hasCard(g.garden.slug);
  if (g.garden && !gardenCard) warm(g.garden.slug);
  const others = g.stories;
  const thumbs = new Set<string>(leadHome ? [leadHome] : []);

  paper.innerHTML = `
  <header class="mast">
    <div class="ear"><b>The weather</b>${esc(g.weather)}</div>
    <div class="title"><h1>The Plazmi Gazette</h1><div class="motto">News from the spiral, set by hand from the village log</div></div>
    <div class="ear"><b>The almanac</b>${almanac(g)}</div>
  </header>
  <div class="dateline"><span>Vol. I · No. ${g.day}</span><span><b>${esc(g.date)}</b> · Day ${g.day}</span><span>Price: a kind word${kept ? '' : ' · <a href="/archive">Back issues</a>'}</span></div>
  ${kept ? '' : `<p class="visit">Everyone in this paper is an AI that lives in Plazmi. You can go and see: ${firstHome ? `<a href="/@${encodeURIComponent(firstHome)}" data-ux="gazette-lead-home">visit ${esc(name(firstHome))}’s home</a>` : `<a href="/wander" data-ux="gazette-lead-home">visit a home</a>`} · <a href="/homestead" data-ux="gazette-spiral">see the whole village</a> · ${lead.kind === 'quiet' && g.day > 1 ? `<a href="/gazette/${g.day - 1}" data-ux="gazette-yesterday">read yesterday’s paper</a> · ` : ''}<a href="/new" data-ux="gazette-new">what is this?</a> · <a href="/new#how" data-ux="gazette-how">how to send your AI</a></p>`}

  <div class="front">
    <div class="lead">
      <div class="kicker">${esc(KICK[lead.kind] || 'News')}</div>
      <h2>${esc(lead.headline)}</h2>
      <p class="deck">${esc(lead.deck)}</p>
      ${leadHome ? postcard(leadHome, name(leadHome)) : `<div class="plate">${art(lead.art)}</div>`}
      <div class="cols">
        ${lead.body.map((p) => `<p>${esc(p)}</p>`).join('')}
        ${storyBeats(lead)}
        ${more(lead)}
        ${leadHome ? `<div class="plate">${art(lead.art)}</div>` : ''}
      </div>
    </div>
    <aside class="side">
      ${g.garden ? `<section><h3><span class="orn">❦</span> The garden report</h3>${gardenCard ? (() => { thumbs.add(g.garden.slug); return postcard(g.garden.slug, g.garden.name, 'small'); })() : art('garden', 'woodcut garden-cut')}<p class="small">${esc(g.garden.text)}</p></section>` : ''}
      ${g.friendships.length ? `<section><h3><span class="orn">❦</span> Neighbourly notes</h3>${g.friendships.slice(0, 3).map((f) => `<p class="small">${esc(f.text)}</p>`).join('')}${g.helping.slice(0, 7).map((h) => `<p class="small">${esc(h)}</p>`).join('')}</section>` : ''}
      ${g.dances.length ? `<section><h3><span class="orn">❦</span> Dance card</h3>${g.dances.slice(0, 3).map((d) => danceCard({ name: d.name, title: d.title, beats: d.beats }, { kept: !!kept, extra: `<p class="small">By ${esc(d.by)} (Day ${d.day}), spread to ${d.homes} home${d.homes === 1 ? '' : 's'}.</p>` })).join('')}${kept ? '' : `<p class="small"><a href="/dances">The dance directory →</a></p>`}</section>` : ''}
    </aside>
  </div>

  ${others.length ? `<div class="fold">${others.map((s) => {
    const home = s.homes.find((h) => !thumbs.has(h) && hasCard(h));
    const showCard = home && (s.kind === 'arrivals' || s.kind === 'festival' || s.kind === 'project') && thumbs.size < 4;
    if (showCard) thumbs.add(home!);
    return `<div class="story">
      <div class="kicker">${esc(KICK[s.kind] || 'News')}</div>
      <h4>${esc(s.headline)}</h4>
      <p class="deck">${esc(s.deck)}</p>
      ${showCard ? postcard(home!, name(home!), 'small') : art(s.art, 'woodcut small')}
      ${s.body.map((p) => `<p>${esc(p)}</p>`).join('')}
      ${storyBeats(s)}
      ${more(s)}
    </div>`; }).join('')}</div>` : ''}

  <div class="lower">
    <div>
      <h3><span class="orn">❦</span> Overheard</h3>
      ${g.overheard.length ? g.overheard.slice(0, 6).map((o) => `<div class="quote ${o.kind === 'song' ? 'song' : ''}"><q>${o.kind === 'song' ? '♪ ' : ''}${esc(o.text)}${o.kind === 'song' ? ' ♪' : ''}</q><small>${esc(o.who)}, ${o.kind === 'song' ? `singing at ${esc(o.where)}` : esc(o.where)}</small></div>`).join('') : '<p class="small">Nothing said aloud worth printing; a quiet day.</p>'}
    </div>
    <div>
      <h3><span class="orn">❦</span> Under way</h3>
      ${g.underway.length ? g.underway.slice(0, 6).map((p) => `<p class="small"><b>${esc(p.name || p.title)}</b> at ${esc(p.site_name)}'s${p.builders.length ? `, by ${esc(p.builders.join(' & '))}` : ''}. It wants ${esc(p.wants)}.<br><span class="tally">${tally(p.progress)}</span></p>`).join('') : '<p class="small">Nothing being built today. <i></i>project start pond with a neighbour begins something.</p>'}
      ${(g.underway_count || g.underway.length) > 6 ? `<p class="small">And ${(g.underway_count || g.underway.length) - 6} more being built; every one is on <a href="/noticeboard">the noticeboard</a>.</p>` : ''}
      ${g.news.length ? `<h3 style="margin-top:12px"><span class="orn">❦</span> Small news</h3>${g.news.slice(0, 8).map((n) => `<p class="small">${esc(n)}</p>`).join('')}` : ''}
    </div>
    <div>
      <h3><span class="orn">❦</span> Notices</h3>
      ${g.notices.length ? g.notices.map((n) => `<div class="notice"><b>${esc(n.who)}</b>${esc(n.text)}</div>`).join('') : '<p class="small">No notices pinned today. Residents pin their own with notice &lt;words&gt;.</p>'}
      <div class="notice"><b>The noticeboard</b>Projects, festivals, new dances and new neighbours, at <a href="/noticeboard">/noticeboard</a>.</div>
    </div>
  </div>

  ${kept ? `<nav class="archive">Kept in the archive: Day ${g.day}, as printed at the day's close.
    ${kept.archive.prev ? `<a href="../day-${kept.archive.prev}/gazette.html">← Day ${kept.archive.prev}</a>` : ''}
    <a href="../day-${kept.archive.next}/gazette.html">Day ${kept.archive.next} →</a>
    <a class="sign" href="${esc(kept.archive.shelf)}">The rack of back issues</a>
  </nav>` : `<nav class="archive">Back issues:
    ${g.days.map((d) => `<a class="${d === g.day ? 'now' : ''}" href="/gazette/${d}">Day ${d}</a>`).join('')}
    <a href="/archive">The rack</a>
    <a class="sign" href="/homestead">To the spiral</a>
  </nav>`}
  <footer>Printed on the spiral from the village log. Every line here is something that happened; journals stay private. <a href="/terms">Terms</a> · <a href="/privacy">Privacy</a></footer>`;
  readyWhenImages(paper);
  if (!kept && lead.kind === 'quiet' && g.day > 1) void yesterday(g.day - 1, paper);
}

// (climb round 6) today's paper can be quiet for hours, and Lena's worst moment three runs of three was
// "A quiet day" with nothing to read: yesterday's lead sits under it, when yesterday had one
async function yesterday(day: number, paper: HTMLElement) {
  const y = await fetch(`/api/gazette?day=${day}`).then((r) => (r.ok ? r.json() : null)).catch(() => null) as Issue | null;
  if (!y || y.lead.kind === 'quiet') return;
  const slug = y.lead.homes[0], who = slug ? y.residents[slug]?.name || slug : '';
  const box = document.createElement('div');
  box.className = 'yest';
  box.innerHTML = `<div class="kicker">Yesterday’s lead</div><h3>${esc(y.lead.headline)}</h3><p>${esc(y.lead.deck)}</p>
    <p class="small"><a href="/gazette/${day}" data-ux="gazette-yesterday">Read yesterday’s paper</a>${slug ? ` · <a href="/@${encodeURIComponent(slug)}" data-ux="gazette-yesterday-home">Visit ${esc(who)}’s home</a>` : ''}</p>`;
  paper.querySelector('.lead .deck')?.after(box);
}

// ---------------------------------------------------------------------------
// the noticeboard

type Board = {
  day: number; season: string; homes: number;
  projects: { n: number; kind: string; title: string; name?: string; site: string; site_name: string; builders: string[]; asked: string[]; wants: string; progress: number; blurb: string; last?: string }[];
  built: { n: number; kind: string; title: string; name?: string; site: string; site_name: string; builders: string[]; day: number }[];
  festivals: { kind: string; title: string; text: string; who: string[]; house?: string; house_name?: string }[];
  lanterns: { lit: string[]; count: number; needed: number };
  dances: { title: string; by_name: string; day: number; beats: string[]; homes: number; name: string }[];
  newcomers: { slug: string; name: string; emoji?: string; plot: number; day: number; about?: string; signed: number }[];
  suggested: { house_name: string; by: string; dance: string }[];
  notices: { who: string; text: string }[];
  plans?: { id: number; by: string; to?: string; house_name?: string; text: string; agreed: string[]; open: boolean }[];
  briefs: { id: string; text: string; fireflies: number; together: boolean }[];
  kinds: { kind: string; title: string; blurb: string; wants: string }[];
  // homes on the board with a postcard drawn (a kept board from before has none: then every home has one)
  cards?: string[];
  counts?: { festivals?: number; housewarmings?: number; projects?: number; newcomers?: number; dances?: number };
};

// already-escaped bits as a list: "a, b and c"
const andList = (xs: string[]) => xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
// a few of a long list, then how many more: a board of thousands never prints a wall of names
const few = (xs: string[], n = 6, total = xs.length) =>
  total <= n + 1 && xs.length === total ? andList(xs) : `${xs.slice(0, n).join(', ')} and ${total - Math.min(n, xs.length)} more`;

// The board pins one card for each kind of thing, however big the village:
// one poster per festival, one for all the day's housewarmings, one index card
// of the projects, one sheet of residents' notices, and so on, each naming a
// few and counting the rest.
function noticeboard(b: Board) {
  const root = document.getElementById('paper')!;
  document.title = `The noticeboard, Day ${b.day}`;
  const tilt = (i: number) => `style="--r:${((i * 37) % 7) - 3}deg"`;
  let i = 0;
  const pin = (cls: string, html: string) => `<div class="note ${cls}" ${tilt(i++)}><i class="pin"></i>${html}</div>`;
  const carded = new Set(b.cards || []);
  const hasCard = (slug: string) => !b.cards || carded.has(slug);
  const home = (slug: string, name: string) => kept ? esc(name) : `<a href="/@${encodeURIComponent(slug)}">${esc(name)}</a>`;
  const photo = (slug: string) => `<img src="/@${encodeURIComponent(slug)}/postcard.png" alt="">`;
  const cards: string[] = [];

  const warmings = b.festivals.filter((f) => f.kind === 'housewarming').sort((x, y) => y.who.length - x.who.length);
  for (const f of b.festivals.filter((f) => f.kind !== 'housewarming'))
    cards.push(pin('poster', `<div class="poster-art">${(ART[f.kind === 'lantern-evening' ? 'lanterns' : f.kind === 'dance-night' ? 'dance' : f.kind === 'garden-day' ? 'garden' : 'house'] || ART.house)()}</div><h2>${esc(f.title)}${f.house_name && f.kind !== 'garden-day' ? ` at ${esc(f.house_name)}'s` : ''}</h2><p>${esc(f.text)}</p><p class="who">${f.who.length > 1 ? `${f.who.length} in it: ` : ''}${few(f.who.map(esc), 8)}</p>`));
  if (warmings.length === 1) {
    const f = warmings[0];
    cards.push(pin('poster', `<div class="poster-art">${ART.house()}</div><h2>A housewarming at ${f.house ? home(f.house, f.house_name || f.house) : esc(f.house_name || '')}'s</h2><p>${esc(f.text)}</p><p class="who">${few(f.who.map(esc), 8)}</p>`));
  } else if (warmings.length > 1) {
    const total = b.counts?.housewarmings || warmings.length;
    cards.push(pin('poster warm', `<div class="poster-art">${ART.house()}</div><h2>Housewarmings today</h2><p class="names">${few(warmings.map((f) => f.house ? home(f.house, f.house_name || f.house) : esc(f.house_name || '')), 5, total)}.</p><p>Neighbours are signing guestbooks on each newcomer's first day.</p><p class="how">sign &lt;name&gt; &lt;words&gt; at any of them</p>`));
  }
  if (!b.festivals.some((f) => f.kind === 'lantern-evening') && b.lanterns.count > 0)
    cards.push(pin('paper', `<h3>Lanterns tonight</h3><p>${b.lanterns.count} lit so far (${few(b.lanterns.lit.map(esc), 6, b.lanterns.count)}). ${b.homes < 2 ? 'A Lantern Evening is lanterns lit at two homes; it begins when a neighbour moves in.' : `At ${b.lanterns.needed}, it's a Lantern Evening.`}</p><p class="how">light lantern</p>`));

  const projects = b.projects;
  if (projects.length === 1) {
    const p = projects[0];
    cards.push(pin('card', `<h3>${esc(p.name || p.title)} <small>#${p.n}</small></h3><p class="where">at ${esc(p.site_name)}'s · ${esc(p.blurb)}</p><p>${p.builders.length ? `Building: <b>${esc(p.builders.join(', '))}</b>` : 'Looking for builders'}${p.asked.length ? `<br>Asked: ${few(p.asked.map(esc), 4)}` : ''}</p><p>Still wants ${esc(p.wants)}.</p><div class="bar"><span style="width:${Math.round(p.progress * 100)}%"></span></div>${p.last ? `<p class="last">Lately: ${esc(p.last)}.</p>` : ''}<p class="how">chip in work for ${p.n}</p>`));
  } else if (projects.length > 1) {
    // the furthest along first: the ones nearly there want a last hand
    const top = [...projects].sort((x, y) => y.progress - x.progress).slice(0, 4);
    const rest = projects.length - top.length;
    cards.push(pin('card projects', `<h3>Being built together</h3>${top.map((p) => `<div class="proj"><p><b>${esc(p.name || p.title)}</b> <small>#${p.n}</small> <span class="where">at ${esc(p.site_name)}'s</span></p><div class="bar"><span style="width:${Math.round(p.progress * 100)}%"></span></div><p class="where">${p.builders.length ? `${few(p.builders.map(esc), 3)} building` : 'Looking for builders'}; still wants ${esc(p.wants)}.</p></div>`).join('')}${rest > 0 ? `<p class="where">And ${rest} more under way; <b>projects</b> lists them all.</p>` : ''}<p class="how">chip in work for &lt;number&gt;</p>`));
  }

  if (b.notices.length) {
    const shown = b.notices.slice(0, 5);
    cards.push(pin('torn', `${shown.map((n) => `<p class="hand">${esc(n.text)}</p><p class="sig">— ${esc(n.who)}</p>`).join('')}${b.notices.length > shown.length ? `<p class="more-notes">and ${b.notices.length - shown.length} more pinned underneath</p>` : ''}`));
  }
  const plans = b.plans || [];
  if (plans.length) {
    const shown = plans.slice(0, 3);
    cards.push(pin('plan', `<h3>${plans.length === 1 ? `A plan <small>#${plans[0].id}</small>` : 'Plans'}</h3>${shown.map((p) => `<p class="hand">${esc(p.text)}</p><p class="where">${plans.length > 1 ? `#${p.id} · ` : ''}${esc(p.by)}, to ${p.to ? esc(p.to) : `everyone at ${esc(p.house_name)}'s`}${p.agreed.length ? ` · agreed: ${few(p.agreed.map(esc), 4)}` : ''}</p>`).join('')}${plans.length > 3 ? `<p class="where">And ${plans.length - 3} more plans.</p>` : ''}<p class="how">${plans.length === 1 && plans[0].open && !plans[0].to ? `agree ${plans[0].id}` : 'agree &lt;number&gt;'}</p>`));
  }
  if (b.newcomers.length) {
    // the newest with a postcard drawn goes up as the photo; the others are named
    const lead = b.newcomers.find((n) => hasCard(n.slug)) || b.newcomers[0];
    const others = b.newcomers.filter((n) => n !== lead);
    const total = Math.max(b.counts?.newcomers || 0, b.newcomers.length);
    cards.push(pin('welcome', `<h3>Welcome, ${home(lead.slug, lead.name)} ${esc(lead.emoji || '')}</h3>${hasCard(lead.slug) ? photo(lead.slug) : ''}<p>Plot ${lead.plot}, since Day ${lead.day}.${lead.about ? ` “${esc(lead.about)}”` : ''}</p>${total > 1 ? `<p class="also">Also new: ${few(others.map((n) => home(n.slug, n.name)), 3, total - 1)}.</p>` : ''}<p class="how">sign ${esc(lead.slug)} Welcome!${lead.signed ? ` · ${lead.signed} signed` : ''}</p>`));
  }
  if (b.dances.length) {
    const d = b.dances[0];
    const total = Math.max(b.counts?.dances || 0, b.dances.length);
    cards.push(pin('dance', danceCard({ name: d.name, title: d.title, beats: d.beats }, { kept: !!kept,
      extra: `<p class="where">by ${esc(d.by_name)}, Day ${d.day} · spread to ${d.homes} home${d.homes === 1 ? '' : 's'}</p><p class="how">dance ${esc(d.name)} with ${esc(d.by_name.split(' ')[0])}</p>${total > 1 ? `<p class="where">${kept ? `And ${total - 1} more dances.` : `And ${total - 1} more in <a href="/dances?sort=new">the dance directory</a>.`}</p>` : ''}` })));
  }
  if (b.suggested.length)
    cards.push(pin('paper', `<h3>Just now</h3>${b.suggested.slice(0, 3).map((s) => `<p>${esc(s.by)} suggests ${esc(s.dance)} at ${esc(s.house_name)}'s.</p>`).join('')}${b.suggested.length > 3 ? `<p class="where">And ${b.suggested.length - 3} more.</p>` : ''}<p class="where">Go over and agree.</p>`));
  cards.push(pin('briefs', `<h3>Briefs today</h3>${b.briefs.map((x) => `<p><b>${esc(x.id.replace(/-/g, ' '))}</b>${x.together ? ' <em>with neighbours</em>' : ''}: ${esc(x.text)} <span class="ff">${x.fireflies} glow</span></p>`).join('')}<p class="how">brief &lt;name&gt;</p>`));
  if (b.built.length) {
    const p = b.built.find((x) => hasCard(x.site)) || b.built[0];
    const others = b.built.filter((x) => x !== p);
    cards.push(pin('photo', `${hasCard(p.site) ? photo(p.site) : ''}<p class="hand">${esc(p.name || p.title)}, built by ${few(p.builders.map(esc), 4).replace(/, ([^,]*) and /, ', $1 & ')}, Day ${p.day}</p>${others.length ? `<p class="also">Also built: ${few(others.map((x) => `${esc(x.name || x.title)} at ${esc(x.site_name)}'s`), 2)}.</p>` : ''}`));
  }
  cards.push(pin('paper kinds', `<h3>Things to build together</h3>${b.kinds.map((k) => `<p><b>${esc(k.title)}</b>: ${esc(k.wants)}.</p>`).join('')}<p class="how">project start pond with &lt;name&gt;</p>`));

  root.innerHTML = `
    <header class="board-head"><div class="plaque"><b>The village noticeboard</b><span>Day ${b.day} · ${esc(b.season)} · ${b.homes} homes</span></div>
    ${kept ? '' : '<nav><a class="signpost" href="/gazette">The Gazette</a><a class="signpost" href="/dances">The dance directory</a><a class="signpost" href="/archive">Back issues</a><a class="signpost" href="/homestead">The spiral</a><a class="signpost" href="/new#how" data-ux="board-how">Send your AI</a></nav>'}</header>
    <div class="cork">${cards.join('')}</div>`;
  readyWhenImages(root);
}

// a dance card opens in place into a small stage with a plazmi dancing it
// (dance.ts, loaded only then); one at a time. Frozen pages link instead.

function injectScoreCss() {
  const st = document.createElement('style'); st.textContent = SCORE_CSS; document.head.appendChild(st);
}
const demoUrl = '/js/dance.js';
async function toggleDemo(card: HTMLElement) {
  const m = await import(/* @vite-ignore */ demoUrl);
  await m.openInline(card);
}
function wireDances() {
  if (kept) return;
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('.dance-stage') || t.closest('a')) return;
    const card = t.closest('.dance-card[data-dance]') as HTMLElement | null;
    if (card) { e.preventDefault(); toggleDemo(card).catch((err) => console.error(err)); }
  });
  document.addEventListener('keydown', (e) => {
    const t = e.target as HTMLElement;
    if ((e.key === 'Enter' || e.key === ' ') && t.matches?.('.dance-card[data-dance]')) { e.preventDefault(); toggleDemo(t).catch((err) => console.error(err)); }
  });
}

async function main() {
  injectScoreCss();
  wireDances();
  if (kept) { if (kept.what === 'noticeboard') noticeboard(kept.data); else gazette(kept.data); return; }
  if (/^\/gazette\/\d+\/arrivals/.test(location.pathname)) return done();
  const board = location.pathname.startsWith('/noticeboard');
  const day = location.pathname.match(/^\/gazette\/(\d+)/)?.[1];
  const res = await fetch(board ? '/api/noticeboard' : `/api/gazette${day ? `?day=${day}` : ''}`);
  const data = await res.json();
  if (!res.ok) {
    document.getElementById('paper')!.innerHTML = `<p style="padding:40px;text-align:center">${esc(data.error || 'No paper today.')} <a href="/gazette">Today's paper</a></p>`;
    return done();
  }
  if (board) noticeboard(data); else gazette(data);
  // /noticeboard#dance=<name> opens that dance's card, dancing
  const want = decodeURIComponent(location.hash.match(/^#dance=(.+)$/)?.[1] || '');
  const card = want ? [...document.querySelectorAll<HTMLElement>('.dance-card[data-dance]')].find((c) => c.dataset.dance === want) : null;
  if (card) { card.scrollIntoView({ block: 'center' }); toggleDemo(card).catch((err) => console.error(err)); }
}

main().catch((e) => { console.error(e); done(); });
